// Reads which feed-widget release a brand's CDN `latest` alias serves, so the
// Feed preview can show it next to the Studio build. The published brands'
// stable loaders point at `latest`, so this is normally what the site runs,
// but a pinned loader can serve another release: the stamp says "Published",
// not "Live". This is a public CDN fetch, not a Studio API call, so it stays
// out of the services/api barrel. The manifest is written by
// apps/feed-widget/deploy/cdn.sh (release_manifest).
import { STUDIO_BUILD } from '../config/buildInfo'

const TRAILING_SLASHES_PATTERN = /\/+$/

export const WIDGET_CDN_BASE_URL =
  (import.meta.env.VITE_WIDGET_CDN_BASE_URL || 'https://cdn.wager.games/wager-studio/v1/widgets').replace(/\/+$/, '')

const BRAND_PATTERN = /^[a-z0-9-]+$/
// cdn.sh refuses any other RELEASE_ID; its default is <TIMESTAMP>-<short sha>.
const SAFE_RELEASE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]*$/
const COMMIT_RELEASE_ID = /^\d{8}T\d{6}Z-([0-9a-f]{7,40})$/
const RELEASE_TIMESTAMP = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/
const DAY_MS = 24 * 60 * 60 * 1000

export function latestManifestUrl(brand, base = WIDGET_CDN_BASE_URL) {
  if (typeof brand !== 'string' || !BRAND_PATTERN.test(brand)) {
    throw new Error(`Invalid widget brand: ${JSON.stringify(brand)}`)
  }
  return `${String(base).replace(TRAILING_SLASHES_PATTERN, '')}/${brand}/latest/manifest.json`
}

// cdn.sh stamps generated_at as `date -u +%Y%m%dT%H%M%SZ`; '' when it is not
// that form or names an impossible time.
function releaseTimestampToIso(value) {
  const match = typeof value === 'string' ? RELEASE_TIMESTAMP.exec(value) : null
  if (!match) return ''
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number)
  const time = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  if (
    time.getUTCFullYear() !== year || time.getUTCMonth() !== month - 1 || time.getUTCDate() !== day ||
    time.getUTCHours() !== hour || time.getUTCMinutes() !== minute || time.getUTCSeconds() !== second
  ) {
    return ''
  }
  return time.toISOString()
}

/**
 * The release a brand's `latest` alias serves, or null when it cannot be read:
 * a network failure, a non-OK response, or a manifest without a usable
 * release_id. `commit` is '' for a hand-named release id, `generatedAt` '' for
 * a missing or malformed generated_at. An invalid brand is a caller bug and
 * rejects (see latestManifestUrl).
 */
export async function fetchLatestWidgetRelease(brand, { fetchImpl = globalThis.fetch, base, signal } = {}) {
  const url = latestManifestUrl(brand, base)
  let manifest
  try {
    const response = await fetchImpl(url, { cache: 'no-store', credentials: 'omit', signal })
    if (!response?.ok) return null
    manifest = await response.json()
  } catch {
    return null
  }
  if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) return null
  const releaseId = manifest.release_id
  if (typeof releaseId !== 'string' || !SAFE_RELEASE_ID.test(releaseId)) return null
  return {
    releaseId,
    commit: COMMIT_RELEASE_ID.exec(releaseId)?.[1] ?? '',
    generatedAt: releaseTimestampToIso(manifest.generated_at),
    environment: typeof manifest.environment === 'string' ? manifest.environment : '',
  }
}

/**
 * One line for the preview: the published (`latest`) release id and its UTC
 * day, or that none was found (a brand without a promoted release, or the CDN
 * unreachable).
 */
export function describePublishedRelease(release) {
  if (!release?.releaseId) return 'No published release found'
  const day = release.generatedAt ? release.generatedAt.slice(0, 10) : ''
  return day ? `Published release ${release.releaseId} (${day})` : `Published release ${release.releaseId}`
}

function timeMs(value) {
  if (!value) return Number.NaN
  return new Date(value).getTime()
}

/**
 * A one-line notice when the published release and the Studio build are at
 * least a day apart, else null. Null too when either side has no usable time.
 */
export function describeReleaseDrift(release, build = STUDIO_BUILD) {
  const releaseMs = timeMs(release?.generatedAt)
  const buildMs = timeMs(build?.time)
  if (Number.isNaN(releaseMs) || Number.isNaN(buildMs)) return null
  const gap = buildMs - releaseMs
  if (gap >= DAY_MS) {
    const days = Math.floor(gap / DAY_MS)
    return `Published release is ${days} ${days === 1 ? 'day' : 'days'} older than the Studio build`
  }
  if (-gap >= DAY_MS) return 'Published release is newer than the Studio build'
  return null
}
